export interface ReadingTimerState {
  version: 1;
  eventId: string;
  userId: number;
  bookId: number;
  bookTitle: string;
  expectedStartingPage: number;
  progressUnit?: "pages" | "elapsed_seconds";
  expectedStartingElapsedSeconds?: number;
  startedAt: string;
  timezoneName: string;
  accruedMilliseconds: number;
  runningSince: string | null;
  finishedAt: string | null;
}

export const READING_TIMER_CHANGED_EVENT = "celsus:reading-timer-changed";

export function timerStorageKey(userId: number): string {
  return `celsus:reading-timer:v1:${userId}`;
}

export function elapsedMilliseconds(timer: ReadingTimerState, now: Date): number {
  const running = timer.runningSince
    ? Math.max(0, now.getTime() - new Date(timer.runningSince).getTime())
    : 0;
  return Math.max(0, timer.accruedMilliseconds + running);
}

export function pauseTimer(timer: ReadingTimerState, now: Date): ReadingTimerState {
  return {
    ...timer,
    accruedMilliseconds: elapsedMilliseconds(timer, now),
    runningSince: null,
  };
}

export function resumeTimer(timer: ReadingTimerState, now: Date): ReadingTimerState {
  return timer.runningSince ? timer : { ...timer, runningSince: now.toISOString() };
}

export function readTimer(userId: number): ReadingTimerState | null {
  const raw = window.localStorage.getItem(timerStorageKey(userId));
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<ReadingTimerState>;
    if (
      value.version !== 1 ||
      value.userId !== userId ||
      typeof value.eventId !== "string" ||
      typeof value.bookId !== "number" ||
      typeof value.bookTitle !== "string" ||
      typeof value.expectedStartingPage !== "number" ||
      typeof value.startedAt !== "string" ||
      typeof value.timezoneName !== "string" ||
      typeof value.accruedMilliseconds !== "number" ||
      (value.runningSince !== null && typeof value.runningSince !== "string") ||
      (value.finishedAt !== null && typeof value.finishedAt !== "string") ||
      !Number.isFinite(new Date(value.startedAt).getTime()) ||
      (value.runningSince !== null &&
        !Number.isFinite(new Date(value.runningSince).getTime())) ||
      (value.finishedAt !== null &&
        !Number.isFinite(new Date(value.finishedAt).getTime()))
    )
      throw new Error("Invalid timer state");
    return value as ReadingTimerState;
  } catch {
    window.localStorage.removeItem(timerStorageKey(userId));
    return null;
  }
}

export function writeTimer(timer: ReadingTimerState): void {
  window.localStorage.setItem(timerStorageKey(timer.userId), JSON.stringify(timer));
  window.dispatchEvent(new CustomEvent(READING_TIMER_CHANGED_EVENT));
}

export function clearTimer(userId: number): void {
  window.localStorage.removeItem(timerStorageKey(userId));
  window.dispatchEvent(new CustomEvent(READING_TIMER_CHANGED_EVENT));
}
